"use client";

import NotFound from "@/assets/images/not-found.png";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import React from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "@phosphor-icons/react";
import coinIcon from "@/assets/icons/coin-icon.webp";
import CustomTooltip from "@/components/CustomTooltip";

export default function GDPPerCapita({
  regionData,
  countryData,
  selectedCountryName,
  selectedRegionName,
}) {
  const searchParams = useSearchParams();
  const selectedCountryId = searchParams.get("country");
  const selectedRegionId = searchParams.get("unicorn_regions");

  // Generate chart data from World Bank API data
  const generateChartData = () => {
    if (regionData && Array.isArray(regionData)) {
      // Region view - show top countries by GDP per capita
      return regionData
        .filter(
          (country) =>
            country.macroeconomic?.gdp > 0 &&
            country.macroeconomic?.population > 0,
        )
        .map((country) => {
          const gdp = country.macroeconomic.gdp;
          const population = country.macroeconomic.population;
          const gdpPerCapita = Math.round(gdp / population);

          return {
            name: country.country_name || "Unknown",
            y: gdpPerCapita,
          };
        })
        .sort((a, b) => b.y - a.y)
        .slice(0, 8); // Show top 8 countries
    } else {
      // No data available
      return [];
    }
  };

  const chartData = generateChartData();
  const categories = chartData.map((item) => item.name);
  const seriesData = chartData.map((item, index) => ({
    y: item.y,
    color: `hsl(${45 + index * 15}, 70%, 50%)`, // Generate colors dynamically
  }));

  const options = {
    chart: {
      type: "column",
      backgroundColor: "transparent",
      style: {
        fontFamily: "inherit",
      },
    },
    title: {
      text: null,
    },
    xAxis: {
      categories: categories,
      lineWidth: 0,
      tickLength: 0,
      labels: {
        style: {
          color: "#fff",
          opacity: 0.6,
          fontSize: "11px",
        },
      },
    },
    yAxis: {
      visible: true,
      gridLineWidth: 1,
      gridLineColor: "rgba(255,255,255,0.1)",
      lineWidth: 0,
      labels: {
        style: {
          color: "#fff",
          opacity: 0.6,
          fontSize: "11px",
        },
        formatter: function () {
          return this.value >= 1000 ? this.value / 1000 + "k" : this.value;
        },
      },
      title: {
        text: null,
      },
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      shared: false,
      useHTML: true,
      backgroundColor: "#161617",
      borderColor: "#333",
      borderWidth: 1,
      borderRadius: 6,
      style: {
        color: "#fff",
        fontSize: "12px",
      },
      formatter: function () {
        const val = this.y >= 1000 ? (this.y / 1000).toFixed(1) + "k" : this.y;
        return `<b>${this.x}</b><br/>GDP Per Capita: <b>$${val}</b><br/><span style="font-size: 10px; opacity: 0.6">Source: World Bank</span>`;
      },
    },
    plotOptions: {
      column: {
        pointPadding: 0.25,
        borderWidth: 0,
        borderRadius: 50,
        pointWidth: 30,
        dataLabels: {
          enabled: true, // ✅ show above bar
          inside: false, // place outside (above)
          y: -3,
          formatter: function () {
            return this.y >= 1000
              ? "$" + (this.y / 1000).toFixed(1) + "k"
              : "$" + this.y;
          },
          style: {
            color: "#fff",
            opacity: 0.7,
            fontSize: "10px",
            fontWeight: "normal",
            textOutline: "none", // remove ugly white border
          },
        },
      },
    },
    credits: {
      enabled: false,
    },
    series: [
      {
        name: "GDP Per Capita",
        data: seriesData,
      },
    ],
  };

  // Generate title based on selection
  const getTitle = () => {
    if (selectedCountryName) {
      return `GDP per Capita in ${selectedCountryName} (in USD)`;
    }
    return "GDP per Capita by Country (in USD)";
  };

  // Render Stat Card for Single Country
  if (selectedCountryId) {
    const gdp = countryData?.macroeconomic?.gdp || 0;
    const population = countryData?.macroeconomic?.population || 1;
    const gdpPerCapita = population > 0 ? Math.round(gdp / population) : 0;
    const hasData = countryData?.macroeconomic?.gdp > 0;

    return (
      <div className='flex flex-col h-full'>
        <div className=''>
          <div className='flex items-center justify-between gap-2 mb-4'>
            <p className='text-base font-medium sm:text-sm'>{getTitle()}</p>
            <CustomTooltip
              trigger={
                <span className='-mt-2 -mr-2 text-white/40'>
                  <Info className='size-5' />
                </span>
              }
              content='GDP per capita (current US$). Source: World Bank. Values use the latest available year for each country.'
            />
          </div>

          {hasData ? (
            <div className='flex items-center gap-3 flex-1 pb-3.5'>
              <div className='relative size-9'>
                <Image src={coinIcon.src} layout='fill' alt='GDP per capita' />
              </div>
              <span className='text-3xl font-bold'>
                ${gdpPerCapita.toLocaleString()}
              </span>
            </div>
          ) : (
            <div className='p-6 py-4 my-4 text-center border border-dashed rounded-lg'>
              <div className='relative block mx-auto aspect-square size-20'>
                <Image
                  objectFit='cover'
                  src={NotFound.src}
                  alt='NotFound Image'
                  layout='fill'
                  className='object-cover'
                />
              </div>
              <p className='mt-3 text-base font-semibold'>Oops, No Data :(</p>
              <p className='mt-1 text-xs text-white/40'>
                No GDP data available
              </p>
            </div>
          )}
        </div>
        <div className='text-center mt-auto py-1 -mx-4 text-[10px] text-white/30 bg-white/[0.02] border-t border-white/5'>
          Source: World Bank
        </div>
      </div>
    );
  }

  return (
    <>
      <div className='flex items-center justify-between mb-4'>
        <p className='text-sm font-medium'>{getTitle()}</p>
        <CustomTooltip
          trigger={
            <span className='-mt-2 -mr-2 text-white/40'>
              <Info className='size-5' />
            </span>
          }
          content='GDP per capita (current US$). Source: World Bank. Values use the latest available year for each country.'
        />
      </div>
      {chartData.length > 0 ? (
        <div className='mt-4 -mx-2.5'>
          <HighchartsReact highcharts={Highcharts} options={options} />
          <div className='text-center mt-auto py-1 -mx-4 text-[10px] text-white/30 bg-white/[0.02] border-t border-white/5'>
            Source: World Bank
          </div>
        </div>
      ) : (
        <div className='p-6 py-4 my-4 text-center border border-dashed rounded-lg'>
          <div className='relative block mx-auto aspect-square size-20'>
            <Image
              objectFit='cover'
              src={NotFound.src}
              alt='NotFound Image'
              layout='fill'
              className='object-cover'
            />
          </div>
          <p className='mt-3 text-base font-semibold'>Oops, No Data :(</p>
          <p className='mt-1 text-xs text-white/40'>No GDP data available</p>
        </div>
      )}
    </>
  );
}
